import { XIcon } from '@heroicons/react/outline'
import React, { useState } from 'react'
import { toast } from 'react-toastify'
import { OtherElementsEnum } from '../../../generated/api'
import { getNameWithoutUnderScores } from '../../../helpers/projectHelper'
import { useActions, useAppState } from '../../../presenter'
import { Checkbox } from '../../atoms/Checkbox/Checkbox'
import { SlideIndicators } from '../../atoms/SlideIndicators/SlideIndicators'
import TextInput from '../../atoms/TextInput/TextInput'
import { Wizard } from '../../atoms/Wizard/Wizard'
import { IndustriesChip } from '../../molecules/IndustriesChip/IndustriesChip'
import { IndustriesEnum } from '../../molecules/NewProjectCreation/IndustriesSelect'
import { CancelProjectWizardModal } from '../Modals/CancelProjectWizardModal'

export const HomeOfficeElementsWizard = () => {
  const { currentModal, wizardProjectCreation, wizardProjectSelectedIndustry } = useAppState()
  const { cleanProjectCreation, displayModalAction, setProjectCreationElements } = useActions()
  const [industry] = useState<IndustriesEnum | undefined>(wizardProjectSelectedIndustry)
  const [officeElements, setOfficeElements] = useState<OtherElementsEnum[]>(
    wizardProjectCreation.industryDetails?.otherElements?.values ?? [],
  )
  const [showOtherbg, setShowOtherbg] = useState<boolean>(
    wizardProjectCreation.industryDetails?.otherElements?.other ? true : false,
  )
  const [otherbg, setOtherbg] = useState<string>(wizardProjectCreation.industryDetails?.otherElements?.other ?? '')
  const selectedElements = wizardProjectCreation.industryDetails?.otherElements
  const [showCancelProjectWizardModal, setShowCancelProjectWizardModal] = useState<boolean>(false)

  const goBack = () => {
    SaveProject()
    displayModalAction('HomeWorkSpaceWizard')
  }

  const onClickNext = () => {
    if (officeElements.length < 1) {
      toast('Select an option to continue', { type: 'error' })
      return
    }

    SaveProject()
    displayModalAction('VisualPreferencesWizard')
  }

  const SaveProject = () => {
    setProjectCreationElements({
      other: otherbg,
      values: officeElements,
    })
  }

  const onClose = () => cleanProjectCreation()

  const onClickExit = () => {
    setShowCancelProjectWizardModal(true)
  }

  const onConfirmLeaveWizardModal = () => {
    cleanProjectCreation()
    displayModalAction('None')
  }

  const onKeepEditingWizardModal = () => {
    setShowCancelProjectWizardModal(false)
  }

  const Header = () => {
    return (
      <>
        <button
          type='button'
          className='rounded-md bg-white text-gray-400 hover:text-gray-500 focus:outline-none'
          onClick={onClickExit}
        >
          <XIcon className='h-6 w-6' aria-hidden='true' />
        </button>
      </>
    )
  }

  const Footer = () => {
    return (
      <>
        <button
          className='bg-[#F1F1F1] items-center text-[#35697D] px-3 rounded-lg font-semibold mr-2 h-10'
          onClick={() => goBack()}
        >
          {'<-'} Back
        </button>
        <button
          className='bg-cta-200 items-center text-white px-3 rounded-lg font-semibold mr-2 h-10'
          onClick={onClickNext}
        >
          Next
        </button>
      </>
    )
  }

  const handleOnChecked = (checked: boolean, value: OtherElementsEnum) => {
    if (!checked) {
      setOfficeElements(prevState => [...prevState, value])
    } else {
      const newGoals = officeElements.filter(f => f !== value)
      setOfficeElements(newGoals)
    }

    if (value === OtherElementsEnum.Other && !checked) {
      setShowOtherbg(true)
    } else if (value === OtherElementsEnum.Other && checked) {
      setShowOtherbg(false)
    }
  }
  return (
    <Wizard
      isOpen={currentModal === 'HomeOfficeElementsWizard'}
      onClose={onClose}
      header={<Header />}
      footer={<Footer />}
      exitModal={
        <CancelProjectWizardModal
          showCancelModal={showCancelProjectWizardModal}
          onConfirmLeave={onConfirmLeaveWizardModal}
          onKeepEditing={onKeepEditingWizardModal}
        />
      }
    >
      {industry
        && (
          <>
            <div className='flex flex-col justify-center items-center w-full sm:w-full'>
              <div className='text-primary-100 text-xl font-bold mb-2 text-center'>
                What home office elements should we consider?
              </div>
              <div className='text-primary-200 text-base font-semibold text-center mb-6'>
                Select all that apply
              </div>
              <div className='text-primary-200 text-base font-semibold text-center mb-6'>
                <IndustriesChip industry={industry} />
              </div>
              <div className='overflow-x-visible grid grid-cols-2 gap-4 scroll-smooth'>
                {(Object.values(OtherElementsEnum) as Array<OtherElementsEnum>).map((element, index) => (
                  <Checkbox
                    key={index}
                    checked={selectedElements?.values.find(f => f === element) ? true : false}
                    text={getNameWithoutUnderScores(element)}
                    value={element}
                    onChange={(checked) => handleOnChecked(checked, element)}
                  />
                ))}
              </div>
              {showOtherbg
                && (
                  <div className='my-6 w-18'>
                    <div className='text-primary-100 text-lg font-bold mb-2 text-center'>
                      What other home office elements should we consider?
                    </div>
                    <TextInput
                      label='Other Elements'
                      hasAutoFocus
                      maxLength={52}
                      defaultValue={selectedElements?.other ?? ''}
                      onChange={(e) => {
                        setOtherbg(e.target.value)
                      }}
                    />
                    <span className='flex flex-grow justify-end text-error font-medium text-xs'>
                    </span>
                  </div>
                )}
            </div>
          </>
        )}
      <SlideIndicators />
    </Wizard>
  )
}
